/**
 * Scan POS P6 -- what the Sale screen does with one SCAN_EVENT frame.
 *
 *   frame shape ok? -> on the CURRENT channel? -> generation matches?
 *     -> duplicate? (resend the SAME ACK, never apply twice)
 *     -> local receiver READY? (else REJECTED SALE_* : nothing changed)
 *     -> product lookup (shared Cart core)
 *     -> still READY on the same channel? (else REJECTED SALE_*)
 *     -> mutate the Cart (shared Cart core) -> ACK APPLIED (only now)
 *
 * Results:
 *   APPLIED                       the Cart really changed (next state committed)
 *   REJECTED + reason             PROVEN no change: SALE_NOT_READY / SALE_PAUSED /
 *                                 SALE_LOCKED / PRODUCT_NOT_FOUND /
 *                                 PRODUCT_LOOKUP_UNAVAILABLE / CART_MUTATION_FAILED
 *   (no ACK at all)               the Cart MAY have changed and that cannot be
 *                                 proven either way: P5 resolves it as UNKNOWN.
 *                                 Never "REJECTED to be safe" -- that would lie.
 *
 * Frames are processed one at a time, in arrival order, so the second of two
 * scans builds on the Cart the first one produced. A frame is never applied
 * twice: the bounded per-surface cache remembers each scan_event_id's
 * outcome and answers a repeat with the same ACK.
 *
 * Nothing here logs a barcode, an event id or a frame.
 */
import { AckBody, ReceiverState } from '../api/scan-pos';

export type RejectReason =
    | 'SALE_NOT_READY' | 'SALE_PAUSED' | 'SALE_LOCKED'
    | 'PRODUCT_NOT_FOUND' | 'PRODUCT_LOOKUP_UNAVAILABLE' | 'CART_MUTATION_FAILED';

export type AckOutcome = { result: 'APPLIED'; reason: null } | { result: 'REJECTED'; reason: RejectReason };

/** A channel binding: which surface + generation a socket was opened for. */
export interface Binding {
    surfaceId: string;
    generation: number;
    /** Identity of the socket instance (a new one per connection). */
    channel: number;
}

export type LookupOutcome =
    | { found: true; product: any; source?: 'LOCAL' | 'API' }
    | { found: false; reason: RejectReason };

/**
 * Latency R1 -- where one frame's time went, in ms from the moment the
 * socket handed it over. Numbers and categories only: no barcode, no event
 * id, no surface id. A stage that did not run is absent.
 */
export interface ScanTiming {
    outcome: 'APPLIED' | RejectReason | 'AMBIGUOUS' | 'DUPLICATE';
    source?: 'LOCAL' | 'API';
    queuedMs: number;          // frame received -> this frame's turn (earlier frames still working)
    lookupMs?: number;
    mutateMs?: number;
    cartMs?: number;           // frame received -> Cart changed (APPLIED only)
    ackMs?: number;            // the ACK request(s), retries included
    totalMs: number;           // frame received -> ACK settled (or given up)
}

/** What applying the Cart change reported. */
export type MutateOutcome =
    | { status: 'APPLIED'; product: any }
    | { status: 'NOT_APPLIED' }        // proven: the Cart was not touched
    | { status: 'AMBIGUOUS' };         // may have changed; must not be reported either way

export interface ReceiverDeps {
    /** The binding of the CURRENT verified channel, or null when none. */
    currentBinding(): Binding | null;
    /** The synchronous local receiver state (set BEFORE any unsafe UI opens). */
    receiverState(): ReceiverState;
    lookup(barcode: string): Promise<LookupOutcome>;
    /** Applies +1 of [product] to the current Cart synchronously. */
    mutate(product: any): MutateOutcome;
    sendAck(body: AckBody): Promise<unknown>;
    onApplied?(product: any): void;
    onRejected?(reason: RejectReason): void;
    onAmbiguous?(): void;
    /** ms to wait before ACK retry n (1-based); default 500 / 1500. */
    retryDelay?(attempt: number): number;
    /** Latency R1: one report per processed frame (guarded: may not change an outcome). */
    onTiming?(timing: ScanTiming): void;
    /** Clock for timings (ms); default performance.now / Date.now. */
    now?(): number;
}

interface Marks {
    receivedAt: number;
    startedAt?: number;
    lookupMs?: number;
    mutateMs?: number;
    cartMs?: number;
    source?: 'LOCAL' | 'API';
}

const EVENT_ID = /^[A-Za-z0-9_-]{16,64}$/;
export const DUPLICATE_CACHE_SIZE = 256;
export const ACK_ATTEMPTS = 3;

const STATE_REASON: Record<Exclude<ReceiverState, 'READY'>, RejectReason> = {
    WAITING: 'SALE_NOT_READY',
    PAUSED: 'SALE_PAUSED',
    LOCKED: 'SALE_LOCKED',
};

type Remembered = AckOutcome | 'IN_PROGRESS' | 'AMBIGUOUS';

export interface ScanEventFrame {
    type: 'SCAN_EVENT';
    scan_event_id: string;
    barcode: string;
    surface_generation: number;
}

export const parseScanEventFrame = (frame: any): ScanEventFrame | null => {
    if (!frame || typeof frame !== 'object' || frame.type !== 'SCAN_EVENT') return null;
    if (typeof frame.scan_event_id !== 'string' || !EVENT_ID.test(frame.scan_event_id)) return null;
    if (typeof frame.barcode !== 'string' || frame.barcode.length < 1 || frame.barcode.length > 128) return null;
    if (typeof frame.surface_generation !== 'number' || !Number.isInteger(frame.surface_generation)) return null;
    return frame as ScanEventFrame;
};

const isClientRefusal = (error: unknown): boolean => {
    const status = (error as any)?.response?.status;
    return typeof status === 'number' && status >= 400 && status < 500;
};

export class ScanEventReceiver {
    private readonly deps: ReceiverDeps;
    private readonly seen = new Map<string, Remembered>();
    private queue: Promise<void> = Promise.resolve();

    constructor(deps: ReceiverDeps) {
        this.deps = deps;
    }

    /** Remembered outcome for an event id (tests / diagnostics). */
    remembered(scanEventId: string): Remembered | undefined {
        return this.seen.get(scanEventId);
    }

    cacheSize(): number {
        return this.seen.size;
    }

    /**
     * Handle one parsed message from the socket bound to [binding]. Returns
     * a promise that settles when this frame (and every earlier one) is done.
     */
    handle(raw: any, binding: Binding): Promise<void> {
        const frame = parseScanEventFrame(raw);
        if (!frame) return this.queue;
        const marks: Marks = { receivedAt: this.now() };
        this.queue = this.queue.then(() => this.process(frame, binding, marks)).catch(() => undefined);
        return this.queue;
    }

    private now(): number {
        if (this.deps.now) return this.deps.now();
        return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
    }

    private report(marks: Marks, outcome: ScanTiming['outcome'], ackMs?: number) {
        if (!this.deps.onTiming) return;
        try {
            const end = this.now();
            const timing: ScanTiming = {
                outcome,
                queuedMs: Math.round((marks.startedAt ?? end) - marks.receivedAt),
                totalMs: Math.round(end - marks.receivedAt),
            };
            if (marks.source) timing.source = marks.source;
            if (marks.lookupMs !== undefined) timing.lookupMs = Math.round(marks.lookupMs);
            if (marks.mutateMs !== undefined) timing.mutateMs = Math.round(marks.mutateMs);
            if (marks.cartMs !== undefined) timing.cartMs = Math.round(marks.cartMs);
            if (ackMs !== undefined) timing.ackMs = Math.round(ackMs);
            this.deps.onTiming(timing);
        } catch {
            // measurement never changes a scan's outcome
        }
    }

    private sameChannel(binding: Binding): boolean {
        const current = this.deps.currentBinding();
        return !!current && current.channel === binding.channel && current.surfaceId === binding.surfaceId
            && current.generation === binding.generation;
    }

    private remember(id: string, value: Remembered) {
        if (this.seen.has(id)) this.seen.delete(id);
        this.seen.set(id, value);
        while (this.seen.size > DUPLICATE_CACHE_SIZE) {
            const oldest = this.seen.keys().next().value as string;
            this.seen.delete(oldest);
        }
    }

    private async process(frame: ScanEventFrame, binding: Binding, marks: Marks): Promise<void> {
        marks.startedAt = this.now();
        // A frame from a socket that is no longer this page's verified channel,
        // or for another generation, is not this surface's to answer.
        if (!this.sameChannel(binding) || frame.surface_generation !== binding.generation) return;

        const previous = this.seen.get(frame.scan_event_id);
        if (previous !== undefined) {
            if (previous !== 'IN_PROGRESS' && previous !== 'AMBIGUOUS') {
                const ackStarted = this.now();
                await this.ack(frame, binding, previous);               // the SAME result again
                this.report(marks, 'DUPLICATE', this.now() - ackStarted);
            }
            return;
        }
        this.remember(frame.scan_event_id, 'IN_PROGRESS');

        const before = this.deps.receiverState();
        if (before !== 'READY') {
            return this.finish(frame, binding, { result: 'REJECTED', reason: STATE_REASON[before] }, marks);
        }

        let lookup: LookupOutcome;
        const lookupStarted = this.now();
        try {
            lookup = await this.deps.lookup(frame.barcode);
        } catch {
            lookup = { found: false, reason: 'PRODUCT_LOOKUP_UNAVAILABLE' };
        }
        marks.lookupMs = this.now() - lookupStarted;
        if (lookup.found && lookup.source) marks.source = lookup.source;
        if (!lookup.found) {
            return this.finish(frame, binding, { result: 'REJECTED', reason: lookup.reason }, marks);
        }

        // The page may have moved on while the lookup was in flight.
        const after = this.deps.receiverState();
        if (after !== 'READY') {
            return this.finish(frame, binding, { result: 'REJECTED', reason: STATE_REASON[after] }, marks);
        }
        if (!this.sameChannel(binding)) {
            // The channel moved (transfer / reconnect) while looking up:
            // nothing was changed, and this page is still the event's
            // historical target, so it can say so truthfully.
            return this.finish(frame, binding, { result: 'REJECTED', reason: 'SALE_NOT_READY' }, marks);
        }

        let mutated: MutateOutcome;
        const mutateStarted = this.now();
        try {
            mutated = this.deps.mutate(lookup.product);
        } catch {
            mutated = { status: 'AMBIGUOUS' };
        }
        marks.mutateMs = this.now() - mutateStarted;
        if (mutated.status === 'NOT_APPLIED') {
            return this.finish(frame, binding, { result: 'REJECTED', reason: 'CART_MUTATION_FAILED' }, marks);
        }
        if (mutated.status === 'AMBIGUOUS') {
            this.remember(frame.scan_event_id, 'AMBIGUOUS');             // never applied again, never ACKed
            this.deps.onAmbiguous?.();
            this.report(marks, 'AMBIGUOUS');
            return;
        }
        marks.cartMs = this.now() - marks.receivedAt;
        this.deps.onApplied?.(mutated.product);
        return this.finish(frame, binding, { result: 'APPLIED', reason: null }, marks);
    }

    private async finish(frame: ScanEventFrame, binding: Binding, outcome: AckOutcome, marks: Marks): Promise<void> {
        this.remember(frame.scan_event_id, outcome);
        if (outcome.result === 'REJECTED') this.deps.onRejected?.(outcome.reason);
        const ackStarted = this.now();
        await this.ack(frame, binding, outcome);
        this.report(marks, outcome.result === 'APPLIED' ? 'APPLIED' : outcome.reason, this.now() - ackStarted);
    }

    /** The SAME evidence, a bounded number of times; never a new Cart change. */
    private async ack(frame: ScanEventFrame, binding: Binding, outcome: AckOutcome): Promise<void> {
        const body: AckBody = {
            scan_event_id: frame.scan_event_id,
            surface_id: binding.surfaceId,
            surface_generation: binding.generation,
            result: outcome.result,
        };
        if (outcome.reason) body.reason = outcome.reason;
        for (let attempt = 1; attempt <= ACK_ATTEMPTS; attempt += 1) {
            try {
                await this.deps.sendAck(body);
                return;
            } catch (error) {
                if (isClientRefusal(error) || attempt === ACK_ATTEMPTS) return;   // a 4xx will not change
                const wait = this.deps.retryDelay ? this.deps.retryDelay(attempt) : (attempt === 1 ? 500 : 1500);
                await new Promise((resolve) => setTimeout(resolve, wait));
            }
        }
    }
}
